/**
 * Responsive breakpoint detection and monitoring.
 * Tracks viewport size and notifies when breakpoint changes.
 */

'use strict';

import type { DotNetObjectReference, BreakpointInfo, BreakpointName } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Internal state for breakpoint functionality.
 */
interface BreakpointState {
    dotnet: DotNetObjectReference | null;
    handler: (() => void) | null;
    current: BreakpointInfo | null;
    rafId: number;
}

const state: BreakpointState = {
    dotnet: null,
    handler: null,
    current: null,
    rafId: 0
};

/**
 * Breakpoint thresholds in pixels.
 * Aligned with common Tailwind CSS breakpoints.
 */
const BREAKPOINT_THRESHOLDS = {
    SM: 640,
    MD: 896,
    LG: 1152,
    XL: 1280
} as const;

/**
 * Compute breakpoint name based on viewport width.
 * 
 * @param width - Viewport width in pixels
 * @returns Breakpoint name
 */
function computeBreakpoint(width: number): BreakpointName {
    if (width < BREAKPOINT_THRESHOLDS.SM) return 'xs';
    if (width < BREAKPOINT_THRESHOLDS.MD) return 'sm';
    if (width < BREAKPOINT_THRESHOLDS.LG) return 'md';
    if (width < BREAKPOINT_THRESHOLDS.XL) return 'lg';
    return 'xl';
}

/**
 * Create breakpoint info from current viewport dimensions.
 * 
 * @returns Breakpoint information object
 */
function createBreakpointInfo(): BreakpointInfo {
    const width = window.innerWidth;
    const height = window.innerHeight;
    return {
        width,
        height,
        name: computeBreakpoint(width)
    };
}

/**
 * Initialize breakpoint watcher (window resize).
 * Throttled via requestAnimationFrame for optimal performance.
 * 
 * @param dotnetHelper - .NET object reference for callbacks
 * @returns True if successfully initialized
 */
export function initialize(dotnetHelper: DotNetObjectReference): boolean {
    try {
        // Remove existing listener to avoid duplicates on re-init
        cleanupListeners();

        state.dotnet = dotnetHelper;
        state.current = createBreakpointInfo();

        state.handler = () => {
            // Throttle to next animation frame to avoid excessive updates
            if (state.rafId) {
                return;
            }

            try {
                state.rafId = requestAnimationFrame(() => {
                    state.rafId = 0;
                    state.current = createBreakpointInfo();
                    safeInvokeSync(dotnetHelper, 'OnBreakpointChanged', state.current);
                });
            } catch {
                // Fallback: if requestAnimationFrame is unavailable
                state.current = createBreakpointInfo();
                safeInvokeSync(dotnetHelper, 'OnBreakpointChanged', state.current);
            }
        };

        window.addEventListener('resize', state.handler);

        // Fire initial notification
        safeInvokeSync(dotnetHelper, 'OnBreakpointChanged', state.current);

        return true;
    } catch {
        return false;
    }
}

/**
 * Get current breakpoint information without initializing.
 * 
 * @returns Current breakpoint info or null if not initialized
 */
export function getCurrent(): BreakpointInfo | null {
    return state.current;
}

/**
 * Clean up breakpoint event listeners and pending animation frames.
 */
function cleanupListeners(): void {
    if (state.handler) {
        try {
            window.removeEventListener('resize', state.handler);
        } catch {
            // Ignore errors during cleanup
        }
    }

    if (state.rafId) {
        try {
            cancelAnimationFrame(state.rafId);
        } catch {
            // Ignore errors during cleanup
        }
    }
}

/**
 * Dispose breakpoint watcher and clear state.
 */
export function dispose(): void {
    try {
        cleanupListeners();
    } finally {
        state.handler = null;
        state.dotnet = null;
        state.current = null;
        state.rafId = 0;
    }
}
